import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosInstance from '../config/Axios';
import { useCustomToast } from '../pages/Contexts/ToastContext';

const KEY = 'birthdaySessionsList';

export const useGetBirthdaySessions = (params = {}, options = {}) => {
  const { showToast } = useCustomToast();
  return useQuery(
    [KEY, params],
    async ({ queryKey }) => (await axiosInstance.get('/birthday/sessions', { params: queryKey[1] })).data,
    {
      keepPreviousData: true,
      // Poll every 30s while a session is PREPARING or ACTIVE so server-side
      // expiry (scheduler → COMPLETED) is reflected without a manual refresh.
      // Returns false when no session is running → zero overhead the rest of the day.
      refetchInterval: (data) => {
        const arr = data?.data ?? data ?? [];
        return arr.some(s => s.status === 'PREPARING' || s.status === 'ACTIVE') ? 30_000 : false;
      },
      onError: (err) => showToast('error', 'Birthday', err.response?.data?.message),
      ...options,
    }
  );
};

export const useGetBirthdayAudioSettings = (options = {}) => {
  const { showToast } = useCustomToast();
  return useQuery(
    'birthdayAudioSettings',
    async () => (await axiosInstance.get('/birthday/audio-settings')).data,
    {
      onError: (err) => showToast('error', 'Birthday', err.response?.data?.message),
      ...options,
    }
  );
};

/** POST /api/birthday/sessions/create.
 *  Required payload: { birthday_person_name, template, device_ids[], duration_minutes }.
 *  `template` is the string enum 'arabic'|'english'|'calm'|'party' (NOT a UUID). */
export const useStartBirthdaySession = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((payload) => axiosInstance.post('/birthday/sessions/create', payload), {
    onSuccess: () => {
      qc.invalidateQueries(KEY);
      showToast('success', 'Birthday', 'Birthday session started!');
    },
    onError: (err) => showToast('error', 'Birthday', err.response?.data?.message),
  });
};

/** POST /api/birthday/sessions/prepare — create a PREPARING session that
 *  guests can upload to via the QR before the celebration runs on screens.
 *  Same body shape as /sessions/create; returns { session_id, session_token,
 *  qr_url, status: 'PREPARING' }. Players are NOT notified yet. */
export const usePrepareBirthdaySession = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((payload) => axiosInstance.post('/birthday/sessions/prepare', payload), {
    onSuccess: () => {
      qc.invalidateQueries(KEY);
      showToast('success', 'Birthday', 'Session prepared — share the QR with guests!');
    },
    onError: (err) => showToast('error', 'Birthday', err.response?.data?.message),
  });
};

/** POST /api/birthday/sessions/{id}/start — transition PREPARING → ACTIVE
 *  and push birthday_start to the assigned players. The Firebase payload
 *  now includes any photos collected during the prepare phase, so the
 *  overlay opens with a populated grid. */
export const useStartPreparedBirthdaySession = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((id) => axiosInstance.post(`/birthday/sessions/${id}/start`), {
    onSuccess: () => {
      qc.invalidateQueries(KEY);
      showToast('success', 'Birthday', 'Celebration started!');
    },
    onError: (err) => showToast('error', 'Birthday', err.response?.data?.message),
  });
};

export const useEndBirthdaySession = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((id) => axiosInstance.post(`/birthday/sessions/${id}/end`), {
    onSuccess: () => {
      qc.invalidateQueries(KEY);
      showToast('success', 'Birthday', 'Birthday session ended.');
    },
    onError: (err) => showToast('error', 'Birthday', err.response?.data?.message),
  });
};

/** GET /api/birthday/sessions/{id}/photos — live photo grid for active session.
 *  Polls every 10s while enabled so the dashboard reflects QR uploads quickly. */
export const useGetBirthdaySessionPhotos = (sessionId, enabled = true) =>
  useQuery(
    ['birthdaySessionPhotos', sessionId],
    async () => (await axiosInstance.get(`/birthday/sessions/${sessionId}/photos`)).data,
    {
      enabled: !!sessionId && enabled,
      refetchInterval: 10_000,
      refetchIntervalInBackground: false,
    }
  );

/** PUT /api/birthday/audio-settings — body shape:
 *  { template_arabic_url, template_english_url, template_calm_url, template_party_url } */
export const useUpdateBirthdayAudioSettings = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(
    (payload) => axiosInstance.put('/birthday/audio-settings', payload),
    {
      onSuccess: () => {
        qc.invalidateQueries('birthdayAudioSettings');
        showToast('success', 'Birthday', 'Audio templates saved.');
      },
      onError: (err) => showToast('error', 'Birthday', err.response?.data?.message),
    }
  );
};

/** GET /folder/media filtered to AUDIO + YOUTUBE — backs the audio template
 *  picker. Returns the paginated `data` array as-is.
 *  `recursive: 1` lists files from every folder, not just root — this
 *  dialog has no folder-navigation UI. */
export const useGetAudioLibrary = (enabled = true) =>
  useQuery(
    'birthdayAudioLibrary',
    async () => (await axiosInstance.get('/folder/media', {
      params: { section: 'MEDIA', type: 'AUDIO,YOUTUBE', recursive: 1, per_page: 200, page: 1 },
    })).data,
    { enabled, staleTime: 30_000 }
  );
